import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ArrowLeft } from 'lucide-react';

const DealerTermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link 
          to="/dealer-program" 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dealer Program
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10">
          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Dealer Program Terms & Conditions</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: February 2026</p>
          
          {/* Quick Summary */}
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Summary</h2>
            <ul className="text-gray-700 space-y-1">
              <li>• Minimum 250 cards per month to maintain dealer status</li>
              <li>• Special volume pricing tiers based on monthly submissions</li>
              <li>• Minimum Grade Protection (MGP) available for qualified submissions</li>
              <li>• Status reviewed monthly based on submission volume</li>
            </ul>
          </div>
          
          {/* Section 1 */}
          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">1</span>
              <h2 className="text-lg font-semibold text-gray-900">Eligibility & Application</h2>
            </div>
            <div className="text-gray-700 space-y-3 pl-9">
              <p>The NICE Grading Dealer Program is available to card dealers, streamers, resellers, and businesses who regularly submit cards for grading.</p>
              <p>Applicants must complete the dealer application form with accurate business information.</p>
              <p>NICE Grading reserves the right to approve or deny any application at our sole discretion.</p>
              <p>Approval is based on factors including business type, expected volume, and sales channels.</p>
              <p>Approved dealers will receive confirmation via email within 3-5 business days.</p>
            </div>
          </div>
          
          {/* Section 2 */}
          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">2</span>
              <h2 className="text-lg font-semibold text-gray-900">Monthly Minimum Requirements</h2>
            </div>
            <div className="text-gray-700 space-y-3 pl-9">
              <p className="text-amber-700 font-medium">Important: Failure to meet the monthly minimum may result in status changes or removal from the program.</p>
              <p><span className="font-medium text-gray-900">Minimum Volume:</span> Dealers must submit a minimum of 250 cards every 30 days to maintain active dealer status.</p>
              <p><span className="font-medium text-gray-900">Grace Period:</span> New dealers have a 60-day grace period from approval to reach the monthly minimum.</p>
              <p><span className="font-medium text-gray-900">Status Review:</span> Dealer accounts are reviewed monthly. Accounts consistently below the minimum may be placed on probation or removed from the program.</p>
              <p><span className="font-medium text-gray-900">Reinstatement:</span> Former dealers may reapply after 90 days if removed for not meeting minimums.</p>
            </div>
          </div>
          
          {/* Section 3 */}
          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">3</span>
              <h2 className="text-lg font-semibold text-gray-900">Pricing Tiers & Benefits</h2>
            </div>
            <div className="text-gray-700 space-y-4 pl-9">
              <p>Dealer pricing is determined by your monthly submission volume:</p>
              
              {/* Simple Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900">Tier</th>
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900">Monthly Volume</th>
                      <th className="text-left py-2 font-semibold text-gray-900">Benefits</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4">Dealer Standard</td>
                      <td className="py-2 pr-4">250-499 cards</td>
                      <td className="py-2">Volume discount pricing, priority processing</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4">Dealer Volume</td>
                      <td className="py-2 pr-4">500-999 cards</td>
                      <td className="py-2">Enhanced pricing, priority processing</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Dealer Premium</td>
                      <td className="py-2 pr-4">1,000+ cards</td>
                      <td className="py-2">Lowest pricing, priority processing, dedicated support</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p>Pricing tiers are automatically adjusted at the time of submission, based on the quantity of cards submitted.</p>
              <p>Custom pricing arrangements may be available for high-volume dealers—contact us to discuss.</p>
              <p>Dealer pricing applies to all submissions while your account is in good standing.</p>
            </div>
          </div>
          
          {/* Section 4 */}
          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">4</span>
              <h2 className="text-lg font-semibold text-gray-900">Dealer Conduct & Compliance</h2>
            </div>
            <div className="text-gray-700 space-y-3 pl-9">
              <p>Certified Dealers agree to represent NICE Grading positively and professionally in all business dealings, including card shows and live streams. Misconduct or mistreatment of NICE may result in immediate termination.</p>
              <p>Submitting counterfeit, altered, or misrepresented cards will result in immediate removal from the program.</p>
              <p>Certified Dealers may not resell or transfer their dealer status or pricing to third parties. Certified Dealers are welcome to submit cards on behalf of collectors as long as minimum quantity thresholds are achieved.</p>
              <p>Any abuse of the dealer program, including but not limited to manipulation of submission volumes or fraudulent activity, will result in immediate termination.</p>
            </div>
          </div>
          
          {/* Section 5 */}
          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">5</span>
              <h2 className="text-lg font-semibold text-gray-900">Program Changes & Termination</h2>
            </div>
            <div className="text-gray-700 space-y-3 pl-9">
              <p>NICE Grading reserves the right to modify dealer pricing, benefits, or program terms at any time with 30 days notice.</p>
              <p>Certified Dealers will be notified of any material changes via email.</p>
              <p>Either party may terminate the dealer relationship with 30 days written notice.</p>
              <p>Upon termination, any pending submissions will be processed at the dealer rate, but future submissions will revert to standard pricing.</p>
            </div>
          </div>
          
          {/* Section 6 */}
          <div>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-2xl font-bold text-gray-300">6</span>
              <h2 className="text-lg font-semibold text-gray-900">Contact & Support</h2>
            </div>
            <div className="text-gray-700 pl-9">
              <p>
                For questions about the Dealer Program or these terms, please contact our dealer support team at dealers[at]nicegrading.com
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DealerTermsPage;
